import { useAjax } from "~/composables/useAjax";
import { EcomService } from "~/services/ecom.service";
import { AppCookieNames } from "../assets/constants/ecomApi";

// Global token refresh lock to prevent multiple concurrent refreshes
let isTokenRefreshInProgress = false;
let tokenRefreshPromise = null;

// Simple client-side cache implementation
const clientCache = new Map();

export const useAjaxEcom = async <T>(
    url: string,
    options?: {
        params?: object;
        method?: "get" | "post" | "put" | "patch" | "delete";
        query?: object;
        headers?: object;
        show_notifications?: boolean;
        show_loading_indicator?: boolean;
        severity?: string;
        debug?: boolean;
        cache?: boolean;
        ttl?: number;
    },
    nuxtApp = null,
    config = null,
    callback = null,
    retries = 4
) => {
    try {
        if (!options) {
            options = {};
        }

        if(import.meta.client && !nuxtApp){
            nuxtApp = useNuxtApp();
        }

        if(import.meta.client && !config){
            config = useRuntimeConfig();
        }

        let key = url;
        if (options.query) {
            key = key + JSON.stringify(options.query);
        } else if (options.params) {
            key = key + JSON.stringify(options.params);
        }

        if(config && config.public){
            key = key+'-'+config.public.appEnvironment+'-'+config.public.version+'-'+config.public.appRegion;
        }

        // Add method to cache key to properly handle different HTTP methods
        if (options.method) {
            key = options.method.toUpperCase() + '-' + key;
        } else {
            key = 'GET-' + key;
        }

        // Check client-side cache for GET requests
        if (import.meta.client && options.cache && (options.method === 'get' || !options.method)) {
            const cachedData = clientCache.get(key);
            if (cachedData && cachedData.expiry > Date.now()) {
                return cachedData.data;
            } else if (cachedData) {
                // Remove expired cache entries
                clientCache.delete(key);
            }
        }

        if (!("show_notifications" in options)) {
            options.show_notifications = false;
        }

        if (!("debug" in options)) {
            options.debug = true;
        }
        if (!options.headers) {
            options.headers = {};
        }

        const authStore = useAuthStore();
        if(!options.headers["X-Toolstation-Customer-Id"]){

            if (authStore.guest && authStore.guest.token) {
                options.headers["X-Toolstation-Customer-Id"] = authStore.guest.token;
            } else if (authStore.user && authStore.user.token) {
                options.headers["X-Toolstation-Customer-Id"] = authStore.user.token;
            }
        }

        if(nuxtApp && nuxtApp.$i18n && nuxtApp.$i18n.locale && nuxtApp.$i18n.locale.value ){
            options.headers['Accept-Language'] = nuxtApp.$i18n.locale.value;
        }

        options.headers['User-Agent'] = `Toolstation EU24-Web`;
        if(import.meta.server && config){
            options.headers['User-Agent'] = options.headers['User-Agent']+` ${config.public.appEnvironment}-${config.public.version}-${config.public.appRegion}`;
        }

        let base_url = import.meta.server && config && config.ecomApiBaseUrlServer ? config.ecomApiBaseUrlServer : config && config.public.ecomApiBaseUrl ? config.public.ecomApiBaseUrl : '';

        let ecom_url = base_url + url;

        if((import.meta.server && (!config || !config.ecomApiBaseUrlServer)) || import.meta.client){
            const accessTokenData = await EcomService.getAccessToken();

            options.headers.authorization = `Bearer ${accessTokenData.accessToken}`;
        }

        if (import.meta.server && options.cache && nuxtApp && nuxtApp.$redis && nuxtApp.$redis.status === 'ready') {
            const data = await EcomService.getRedisDataByKey(nuxtApp.$redis,key);
            if(data){
                const parsedData = JSON.parse(data);
                if(parsedData && (parsedData.length > 0 || (typeof parsedData === 'object' && Object.keys(parsedData).length > 0))){
                    return parsedData;
                }
            }
        }

        let response = await useAjax<T>(ecom_url, options, callback);

        if (import.meta.client && response && response.success === false && response.data &&
            response.data.error && response.data.error.code && response.data.error.code.includes('401.') &&
            response.data.error.message === AppCookieNames.EcomApiTokenErrorMessage && retries > 0) {

            // Decrement retries before any async operations to prevent race conditions
            retries--;

            try {
                // Use a shared promise for concurrent token refreshes
                if (!isTokenRefreshInProgress) {
                    isTokenRefreshInProgress = true;
                    tokenRefreshPromise = EcomService.refreshToken(true)
                        .finally(() => {
                            isTokenRefreshInProgress = false;
                            tokenRefreshPromise = null;
                        });
                }

                // Wait for the shared token refresh to complete
                await tokenRefreshPromise;

                // Get fresh token and retry the request
                const accessTokenData = await EcomService.getAccessToken();
                options.headers.authorization = `Bearer ${accessTokenData.accessToken}`;

                // Retry the request directly without recursion
                response = await useAjax<T>(ecom_url, options, callback);
            } catch (refreshError) {
                console.error("Token refresh failed during API call:", refreshError);
                // Return the original error response if token refresh fails
                return response;
            }
        }

        // Server-side caching
        if(import.meta.server && response && response.success !== false && options.cache && nuxtApp && nuxtApp.$redis && nuxtApp.$redis.status === 'ready'){
            if(!options.ttl){
                options.ttl = 90
            }
            nuxtApp.$redis.set(key,JSON.stringify(response), "EX", options.ttl);
        }

        // Client-side caching for GET requests
        if (import.meta.client && response && response.success !== false && options.cache &&
            (options.method === 'get' || !options.method)) {
            const ttl = options.ttl || 90; // Default TTL of 90 seconds
            clientCache.set(key, {
                data: response,
                expiry: Date.now() + (ttl * 1000)
            });

            // Clean up expired cache entries periodically
            if (clientCache.size > 100) {
                const now = Date.now();
                for (const [cacheKey, cacheValue] of clientCache.entries()) {
                    if (cacheValue.expiry < now) {
                        clientCache.delete(cacheKey);
                    }
                }
            }
        }

        return response;
    } catch (error) {
        console.log("[useAjaxEcomError]",error.message);
        console.log("[useAjaxEcomError]",error);

        return {
            success: false,
            errors: error.message,
        };
    }
};